/**
 * Armazenamento de autenticação com suporte a "Lembre-me" (5 horas).
 * - Com "Lembre-me": token e user em localStorage com expiração de 5h.
 * - Sem "Lembre-me": token e user em sessionStorage (sessão do navegador).
 */

const TOKEN_KEY = "token";
const USER_KEY = "user";
const EXPIRES_AT_KEY = "auth_expires_at";

/** Duração do "Lembre-me" em milissegundos (5 horas) */
const REMEMBER_ME_DURATION_MS = 5 * 60 * 60 * 1000;

function clearLocalAuth() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(EXPIRES_AT_KEY);
  } catch {
    // ignore
  }
}

function clearSessionAuth() {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
  } catch {
    // ignore
  }
}

/**
 * Retorna o token atual. Se estiver em localStorage e expirado, limpa e retorna null.
 */
export function getToken(): string | null {
  const fromSession = sessionStorage.getItem(TOKEN_KEY);
  if (fromSession) return fromSession;

  const expiresAt = localStorage.getItem(EXPIRES_AT_KEY);
  if (expiresAt) {
    const expiry = parseInt(expiresAt, 10);
    if (Number.isNaN(expiry) || Date.now() >= expiry) {
      clearLocalAuth();
      return null;
    }
  }
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Retorna o user (JSON string) atual. Consistente com o armazenamento do token (sessão ou local com expiração).
 */
export function getUser(): string | null {
  const fromSession = sessionStorage.getItem(USER_KEY);
  if (fromSession) return fromSession;

  const expiresAt = localStorage.getItem(EXPIRES_AT_KEY);
  if (expiresAt) {
    const expiry = parseInt(expiresAt, 10);
    if (Number.isNaN(expiry) || Date.now() >= expiry) {
      clearLocalAuth();
      return null;
    }
  }
  return localStorage.getItem(USER_KEY);
}

/**
 * Salva token e user. Se rememberMe, usa localStorage com expiração de 5 horas; senão sessionStorage.
 */
export function setAuth(token: string, user: object, rememberMe: boolean): void {
  clearLocalAuth();
  clearSessionAuth();
  const userStr = typeof user === "string" ? user : JSON.stringify(user);

  if (rememberMe) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, userStr);
    localStorage.setItem(EXPIRES_AT_KEY, String(Date.now() + REMEMBER_ME_DURATION_MS));
  } else {
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(USER_KEY, userStr);
  }
}

/**
 * Remove token e user de localStorage e sessionStorage.
 */
export function clearAuth(): void {
  clearLocalAuth();
  clearSessionAuth();
}

/**
 * Atualiza apenas o user no mesmo armazenamento em que o token está (session ou local).
 * Útil após editar perfil. Não faz nada se não houver token.
 */
export function updateUser(user: object): void {
  const userStr = typeof user === "string" ? user : JSON.stringify(user);
  if (sessionStorage.getItem(TOKEN_KEY)) {
    sessionStorage.setItem(USER_KEY, userStr);
  } else if (localStorage.getItem(TOKEN_KEY)) {
    const expiresAt = localStorage.getItem(EXPIRES_AT_KEY);
    if (expiresAt && Date.now() >= parseInt(expiresAt, 10)) {
      clearLocalAuth();
      return;
    }
    localStorage.setItem(USER_KEY, userStr);
  }
}
